import { startCase } from "lodash";

import {
  INVISIBLE_MARKS_REGEX,
  TABLE_TRUNCATE_LEN,
  WHITESPACE_SEQUENCE_REGEX,
} from "../constants";

/* eslint-disable @typescript-eslint/no-magic-numbers */
interface ITrimmedCvs {
  display: string;
  content: string;
}

const MAX_GRID_STRING_LENGTH = 25;

export const truncateString = (
  str: string | null | undefined,
  num = MAX_GRID_STRING_LENGTH,
): string => {
  if (!str) {
    return "";
  }

  if (!num) {
    return str;
  }

  if (str.length > num) {
    const subStr = str.substring(0, num);
    return subStr + "...";
  } else {
    return str;
  }
};

export const isTruncateNeeded = (
  str: string | null | undefined,
  num = TABLE_TRUNCATE_LEN,
): boolean => {
  if (!str || !num) {
    return false;
  }

  return str.length > num;
};

export const parseBoolean = (value: unknown): boolean => {
  if (value === true || value === 1) {
    return true;
  }
  if (
    value === false ||
    value === 0 ||
    value === null ||
    value === undefined ||
    value === "false"
  ) {
    return false;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === "true" ||
      normalized === "1" ||
      normalized === "yes" ||
      normalized === "on"
    );
  }
  return false;
};

export const hashString = (value: string): number => {
  let hash = 0;
  let i;
  let chr;

  if (value.length === 0) {
    return hash;
  }

  for (i = 0; i < value.length; i++) {
    chr = value.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + chr;
    // eslint-disable-next-line no-bitwise
    hash |= 0;
  }

  return hash;
};

export const trimCsvForGrid = (names: string[]): ITrimmedCvs => {
  let trimmed = false;

  if (names.length === 0) {
    const empty: ITrimmedCvs = { display: "", content: "" };
    return empty;
  }

  const items = names.reduce(
    (acc, name) => {
      if (acc.count + name.length < MAX_GRID_STRING_LENGTH) {
        acc.count += name.length;
        acc.passed.push(name);
      } else {
        trimmed = true;
      }

      return acc;
    },
    { count: 0, passed: [] } as { count: number; passed: string[] },
  );

  const trimmedCsv: ITrimmedCvs = {
    display: items.passed.join(", ") + (trimmed ? "..." : ""),
    content: names.join(", "),
  };
  return trimmedCsv;
};

export const capitalize = (value: string): string => {
  return value?.replace(/^\w/, c => c.toUpperCase());
};

export const firstLetterToLowerCase = (value: string): string => {
  return value?.replace(/^\w/, c => c.toLowerCase());
};

export const isNumericString = (value: string): boolean => {
  return /^\d+$/.test(value);
};

/**
 * Normalizes user-authored text (file headers, pasted values) for equality matching:
 * drops invisible marks, collapses whitespace and lowercases, so "  Worker " and
 * "worker" resolve to the same lookup key. Not for display — only for comparison.
 */
export const normalizeTextForMatch = (value: string | undefined): string => {
  if (!value) {
    return "";
  }

  return value
    .replace(INVISIBLE_MARKS_REGEX, "")
    .replace(WHITESPACE_SEQUENCE_REGEX, " ")
    .trim()
    .toLowerCase();
};

export const combineStrings = (
  str1: string,
  str2: string | null | undefined,
  delimiter = " ",
  postfix = "",
): string => {
  if (!str2 || str2 === str1) {
    return str1;
  }

  if (!str1 && str2) {
    return str2;
  }

  return `${str1}${delimiter}${str2}${postfix}`;
};

export const isNullOrEmptySpace = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  str: string | null | undefined | number | any[],
): boolean => {
  if (str === null || str === undefined || str === "") {
    return true;
  }

  if (typeof str === "number") {
    str = str + "";
  }

  if (typeof str === "string") {
    return (
      str.trim?.() === "" ||
      str === "undefined undefined" /*Old system sync data deleted*/ ||
      str === "undefined" ||
      str === "null" // Required for RYDE-3673
    );
  }

  if (Array.isArray(str)) {
    return str.length === 0;
  }

  return false;
};

export const removeSlash = (input: string | undefined): string => {
  if (!input || isNullOrEmptySpace(input)) {
    return "";
  }

  return input.replace(/\//g, "");
};

export const removeSpaces = (input: string | number | undefined): string => {
  if (!input || isNullOrEmptySpace(input)) {
    return "";
  }

  if (typeof input === "number") {
    return String(input);
  }

  return input.replace(/\s/g, "");
};

export const caseHelper = (
  input: string,
  letterCase: "lower" | "start" | "upper" | undefined,
): string => {
  let output;

  switch (letterCase) {
    case "lower":
      output = input.toLowerCase();
      break;
    case "start":
      output = startCase(input);
      break;
    case "upper":
      output = input.toUpperCase();
      break;
    default:
      output = input;
      break;
  }

  return output;
};

export const trimStartSlashes = (value: string): string =>
  value?.replace(/^\/+/, "") || "";

/** Characters (Unicode code points), the unit Postgres measures `varchar(n)` in; `String.length` counts an emoji as two. */
export const getCharacterCount = (value: string): number =>
  Array.from(value).length;
